/**
 * FERP Portal - Photo Compliance Upload
 *
 * LIFE-CRITICAL: Upload facility photos for LLM vision analysis
 * against 245D physical space requirements.
 *
 * Uses ChaosDocumentUploader for base upload functionality.
 * Findings from Bedrock Claude vision require human review
 * before becoming compliance records (HITL pattern).
 *
 * Pattern source: ChaosDocumentUploader (file upload)
 */

import { useState, useMemo } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Group,
  Stack,
  Badge,
  ThemeIcon,
  Alert,
  SimpleGrid,
  Loader,
  Button,
  Select,
} from '@mantine/core';
import {
  IconCamera,
  IconAlertTriangle,
  IconInfoCircle,
  IconCheck,
  IconX,
  IconQuestionMark,
  IconUpload,
  IconPhoto,
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useTenantId } from '@/auth';
import { ChaosDocumentUploader } from '@ferp/workflow-forms';
import { useCompleteTasksByToolHintMutation } from '@/generated/graphql';

type AreaCategory =
  | 'entry_exit'
  | 'common_area'
  | 'sleeping'
  | 'bathroom'
  | 'kitchen'
  | 'medication_storage'
  | 'fire_safety';

interface PhotoResult {
  area: AreaCategory;
  fileName: string;
  uploadedAt: string;
  analysisStatus: 'pending' | 'analyzing' | 'complete';
}

const AREA_OPTIONS: { value: AreaCategory; label: string; description: string }[] = [
  { value: 'entry_exit', label: 'Entry/Exit', description: 'Entrances, exits, accessibility ramps' },
  { value: 'common_area', label: 'Common Areas', description: 'Living rooms, dining areas, activity spaces' },
  { value: 'sleeping', label: 'Sleeping Areas', description: 'Bedrooms, minimum room size compliance' },
  { value: 'bathroom', label: 'Bathrooms', description: 'Grab bars, accessibility, cleanliness' },
  { value: 'kitchen', label: 'Kitchen', description: 'Food storage, preparation areas, sanitation' },
  { value: 'medication_storage', label: 'Medication Storage', description: 'Locked storage, temperature monitoring' },
  { value: 'fire_safety', label: 'Fire Safety', description: 'Extinguishers, exits, sprinklers, signage' },
];

export function PhotoComplianceUpload() {
  const tenantId = useTenantId();
  const [selectedArea, setSelectedArea] = useState<AreaCategory | null>(null);
  const [uploadedPhotos, setUploadedPhotos] = useState<PhotoResult[]>([]);

  // DRR-934: Auto-complete onboarding tasks when tool saves successfully
  const [completeTasksByToolHint] = useCompleteTasksByToolHintMutation();

  const areaStats = useMemo(() => {
    const stats: Record<string, number> = {};
    for (const photo of uploadedPhotos) {
      stats[photo.area] = (stats[photo.area] ?? 0) + 1;
    }
    return stats;
  }, [uploadedPhotos]);

  const coveredAreas = Object.keys(areaStats).length;
  const totalAreas = AREA_OPTIONS.length;

  if (!tenantId) {
    return (
      <Container size="lg" py="xl">
        <Alert icon={<IconInfoCircle size={16} />} color="yellow" title="No Tenant Selected">
          Please select a tenant for photo compliance.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <div>
            <Title order={2}>Photo Compliance</Title>
            <Text c="dimmed" size="sm">
              Upload facility photos for LLM vision analysis against physical space requirements
            </Text>
          </div>
          <Badge color="orange" variant="light" size="lg">
            {coveredAreas}/{totalAreas} areas documented
          </Badge>
        </Group>

        {/* Advisory Notice */}
        <Alert icon={<IconAlertTriangle size={16} />} color="blue" title="Advisory Only">
          LLM vision findings are advisory. A compliance coordinator must review
          and approve all findings before they become compliance records.
        </Alert>

        {/* Area Selection Cards */}
        <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }}>
          {AREA_OPTIONS.map((area) => {
            const count = areaStats[area.value] ?? 0;
            const isSelected = selectedArea === area.value;

            return (
              <Card
                key={area.value}
                withBorder
                p="sm"
                onClick={() => setSelectedArea(area.value)}
                style={{
                  cursor: 'pointer',
                  borderColor: isSelected ? 'var(--mantine-color-blue-6)' : undefined,
                  borderWidth: isSelected ? 2 : undefined,
                }}
              >
                <Stack gap={4}>
                  <Group justify="space-between">
                    <ThemeIcon
                      variant="light"
                      size="sm"
                      color={count > 0 ? 'green' : 'gray'}
                    >
                      {count > 0 ? <IconCheck size={14} /> : <IconPhoto size={14} />}
                    </ThemeIcon>
                    {count > 0 && (
                      <Badge size="xs" color="green" variant="light">
                        {count} photo{count !== 1 ? 's' : ''}
                      </Badge>
                    )}
                  </Group>
                  <Text size="sm" fw={500}>{area.label}</Text>
                  <Text size="xs" c="dimmed" lineClamp={2}>{area.description}</Text>
                </Stack>
              </Card>
            );
          })}
        </SimpleGrid>

        {/* Upload Section */}
        {selectedArea ? (
          <Card withBorder p="md">
            <Stack gap="md">
              <Group gap="xs">
                <IconCamera size={20} />
                <Text fw={500}>
                  Upload: {AREA_OPTIONS.find((a) => a.value === selectedArea)?.label}
                </Text>
              </Group>

              <ChaosDocumentUploader
                onUploadComplete={async (results) => {
                  const newPhotos: PhotoResult[] = results.map((r) => ({
                    area: selectedArea,
                    fileName: r.document.metadata.filename ?? 'photo',
                    uploadedAt: new Date().toISOString(),
                    analysisStatus: 'pending' as const,
                  }));
                  setUploadedPhotos((prev) => [...prev, ...newPhotos]);

                  // DRR-934: Auto-complete onboarding tasks that use this tool
                  try {
                    const result = await completeTasksByToolHint({
                      variables: { toolHint: 'photo_compliance_upload' },
                    });
                    const completedCount = result.data?.completeTasksByToolHint ?? 0;
                    if (completedCount > 0) {
                      notifications.show({
                        title: 'Onboarding tasks completed',
                        message: `${completedCount} task(s) auto-completed`,
                        color: 'teal',
                      });
                    }
                  } catch (err) {
                    // Non-fatal — photos are already uploaded
                    console.warn('DRR-934: Failed to auto-complete tasks:', err);
                  }
                }}
                sourceSystem="photo-compliance"
                maxFiles={10}
                maxFileSize={20 * 1024 * 1024}
                acceptedFileTypes={{
                  'image/jpeg': ['.jpg', '.jpeg'],
                  'image/png': ['.png'],
                  'image/heic': ['.heic'],
                }}
              />
            </Stack>
          </Card>
        ) : (
          <Card withBorder p="lg">
            <Text ta="center" c="dimmed" py="xl">
              Select a facility area above to begin uploading photos.
            </Text>
          </Card>
        )}

        {/* Uploaded Photos Summary */}
        {uploadedPhotos.length > 0 && (
          <Card withBorder p="md">
            <Stack gap="sm">
              <Text fw={500}>Uploaded Photos ({uploadedPhotos.length})</Text>
              {AREA_OPTIONS.filter((a) => areaStats[a.value]).map((area) => (
                <Group key={area.value} justify="space-between">
                  <Group gap="xs">
                    <IconPhoto size={14} />
                    <Text size="sm">{area.label}</Text>
                  </Group>
                  <Badge variant="light" color="green" size="sm">
                    {areaStats[area.value]} photo{areaStats[area.value] !== 1 ? 's' : ''}
                  </Badge>
                </Group>
              ))}
            </Stack>
          </Card>
        )}
      </Stack>
    </Container>
  );
}

export default PhotoComplianceUpload;
